import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import configData from "../../../config.json";
import ParticleBackground from "../../common/ParticleBackground";

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const [emailVerified, setEmailVerified] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [emailExists, setEmailExists] = useState(true);

  const [timer, setTimer] = useState(60);
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);

  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0 && !otpVerified) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (timer === 0) {
      clearInterval(interval);
      setOtpExpired(true);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer, otpVerified]);

  useEffect(() => {
    localStorage.clear();
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    try {
      const url = `${configData.SERVER_URL}/forget_password`;
      const secretKey = configData.SECRET_Key;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, secretKey }),
      });
      const data = await response.json();
      const resultData = JSON.parse(data.result);

      if (resultData.success === false) {
        setEmailExists(false);
        return;
      }

      setEmailExists(true);
      setEmailVerified(true);
      setOtpSent(true);
      setTimer(60);
      Swal.fire({ icon: "success", title: "OTP Sent!", text: "Please check your email" });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleValidateOtp = async () => {
    try {
      const url = `${configData.SERVER_URL}/validate_otp`;
      const secretKey = configData.SECRET_Key;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, secretKey }),
      });
      const data = await response.json();
      const resultData = typeof data.result === "string" ? JSON.parse(data.result) : data;

      if (resultData.success === true) {
        setOtpVerified(true);
        setTimer(0);
        Swal.fire({ icon: "success", title: "OTP Verified", text: "Please reset your pin" });
      } else {
        Swal.fire({ icon: "error", title: "Error", text: resultData.message || "Invalid OTP" });
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({ icon: "error", title: "Error", text: "Unable to verify OTP" });
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    if (newPin !== confirmPin) {
      Swal.fire({ icon: "warning", text: "Pins do not match" });
      return;
    }
    try {
      const url = `${configData.SERVER_URL}/update_password`;
      const secretKey = configData.SECRET_Key;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: newPin, confirmPassword: confirmPin, secretKey }),
      });
      const data = await response.json();
      const resultData = JSON.parse(data.result);

      if (resultData.success === true) {
        Swal.fire({ icon: "success", title: "Pin Updated" }).then(() => navigate("/"));
      } else {
        Swal.fire({ icon: "error", title: resultData.message });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="login-page" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/background.jpg)` }}>
      <ParticleBackground />
      <div className="login-card">
        {/* Logo */}
        <div className="login-logo">
          <img src="company_logo.png" alt="Company Logo" />
        </div>

        {/* Title */}
        <div className="login-header">
          <h1>Reset Pin</h1>
          <p>We'll send you an OTP to reset your pin</p>
        </div>

        {/* Step 1: Enter Email */}
        {!emailVerified && (
          <form onSubmit={handleSendOtp}>
            <div className="login-field">
              <label>Email</label>
              <input
                autoFocus
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
              {!emailExists && <span className="login-error">Email does not exist</span>}
            </div>
            <button type="submit" className="login-btn">Send OTP</button>
          </form>
        )}

        {/* Step 2: Enter OTP */}
        {emailVerified && !otpVerified && (
          <form onSubmit={(e) => { e.preventDefault(); handleValidateOtp(); }}>
            <div className="login-field">
              <label>Enter OTP</label>
              <input
                autoFocus
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                required
              />
              {timer > 0 && <span className="login-timer">{formatTime(timer)}</span>}
              {otpExpired && <span className="login-error">OTP expired. Click resend.</span>}
            </div>

            <button type="submit" className="login-btn" disabled={timer === 0}>
              Validate OTP
            </button>

            <div className="login-resend">
              <button
                type="button"
                className="login-resend-btn"
                onClick={handleSendOtp}
                disabled={timer > 0}
              >
                Resend OTP
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Set New Pin */}
        {otpVerified && (
          <form onSubmit={handleResetPin}>
            <div className="login-field">
              <label>New Pin</label>
              <div className="login-pin-wrapper">
                <input
                  type={showNewPin ? "text" : "password"}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  placeholder="Enter new pin"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowNewPin(!showNewPin)}
                  tabIndex={-1}
                >
                  {showNewPin ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                </button>
              </div>
            </div>

            <div className="login-field">
              <label>Confirm New Pin</label>
              <div className="login-pin-wrapper">
                <input
                  type={showConfirmPin ? "text" : "password"}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  placeholder="Confirm new pin"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowConfirmPin(!showConfirmPin)}
                  tabIndex={-1}
                >
                  {showConfirmPin ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                </button>
              </div>
            </div>

            <button type="submit" className="login-btn">Reset Pin</button>
          </form>
        )}

        {/* Back to Login */}
        <div className="login-back">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
            Back to Login
          </a>
        </div>
      </div>

      <p className="login-footer">&copy; {new Date().getFullYear()} O2B Technologies</p>
    </div>
  );
};

export default ForgotPassword;
